import { createContext, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import axios, { SESSION_EXPIRED_EVENT, setDevToken } from 'utils/axios';
import Loader from 'components/Loader';

type AuthContextType = {
  isLoggedIn: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  // The token is an HttpOnly cookie, so the only way to know whether we are signed in
  // is to ask the backend. Nothing is rendered until that answers.
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get('/api/v1/auth/me')
      .then(() => !cancelled && setIsLoggedIn(true))
      .catch(() => !cancelled && setIsLoggedIn(false)) // 401 = signed out
      .finally(() => !cancelled && setIsInitialized(true));

    const onExpired = () => setIsLoggedIn(false);
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => {
      cancelled = true;
      window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
    };
  }, []);

  // Same login endpoint as app.nuway.co. No role check here: this app has no roles,
  // and the backend enforces platform-admin on every admin endpoint.
  const login = useCallback(async (email: string, password: string) => {
    const { data } = await axios.post('/api/v1/auth/authenticate', { email, password });
    setDevToken(data?.token); // local dev only — a no-op on real hosts, where the cookie is the session
    setIsLoggedIn(true);
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post('/api/v1/auth/logout');
    } catch {
      // even if the call fails, drop the local session state
    }
    setDevToken(null);
    setIsLoggedIn(false);
  }, []);

  const value = useMemo(() => ({ isLoggedIn, login, logout }), [isLoggedIn, login, logout]);

  if (!isInitialized) return <Loader />;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
